"use client";

import React, { CSSProperties } from "react";
import { CardPreviewSizing } from "utils/styles";

interface TradePreviewCardSkeletonProps {
  width?: number | string;
  height?: number | string;
  /**
   * Mirror the cell's `hideUser` — also implicitly hides the time, so the
   * footer collapses and only the action button remains in the panel.
   */
  hideUser?: boolean;
  /** Mirror the cell's `hideTime`. (Implicit when `hideUser`.) */
  hideTime?: boolean;
  /** Mirror the cell's `hideAction` — drops the action-button skeleton. */
  hideAction?: boolean;
  /** Mirror the cell's `fromMatch` — shows a ribbon placeholder. */
  fromMatch?: boolean;
}

/**
 * Skeleton for the TradeItemGridCell. Mirrors the real cell's anatomy so
 * the page doesn't jump as data loads — card image with a chip-shaped
 * placeholder pinned to its bottom (and an optional ribbon at top-left),
 * then a borderless panel with an optional footer row and an optional
 * full-width action button.
 *
 * Hide-prop logic mirrors the cell: `hideUser` implies `hideTime`,
 * `hideAction` removes the button, and when both the footer and the
 * action are suppressed the panel doesn't render at all (matches the
 * owner-on-own-list state).
 */
export function TradePreviewCardSkeleton({
  width = CardPreviewSizing.width,
  height,
  hideUser = false,
  hideTime = false,
  hideAction = false,
  fromMatch = false,
}: TradePreviewCardSkeletonProps) {
  const isPercentage = typeof width === "string" && width.includes("%");
  const isFixedUnits =
    typeof width === "string" &&
    (width.includes("in") || width.includes("rem") || width.includes("em"));
  const isPixels = typeof width === "number";

  // Outer container holds the image + (optional) panel stacked
  // vertically. For percentage / fixed-unit widths only the image gets
  // the aspect-ratio — keeps the panel inside the bounding box so grids
  // size rows to the cell's full natural height instead of clipping.
  const containerStyle: CSSProperties = isPercentage
    ? { width }
    : {
        width: isPixels ? `${width}px` : width,
        ...(isPixels && height
          ? { height: typeof height === "number" ? `${height}px` : height }
          : {}),
      };

  const imageWrapperStyle: CSSProperties = isPercentage
    ? { width: "100%", aspectRatio: "2.5/3.5" }
    : isFixedUnits
      ? { width: "100%", aspectRatio: "2.5/3.5" }
      : { height: "60%" };

  // Same conditional logic as TradeItemGridCell.
  const showTime = !hideTime && !hideUser;
  const showFooter = !hideUser || showTime;
  const showPanel = showFooter || !hideAction;

  return (
    <div className="relative flex flex-col" style={containerStyle}>
      {/* Image stage with chip + ribbon overlays. Dimensions mirror the
          real cell (default `badge` size now — no `lg:badge-lg`
          responsive bump) so the loading state doesn't shift when
          content arrives. */}
      <div className="relative" style={imageWrapperStyle}>
        <div className="skeleton h-full w-full rounded-lg" />
        {fromMatch && (
          <div className="bg-base-content/30 absolute left-0 top-[12%] h-5 w-20 rounded-r-full" />
        )}
        {/* Spec chip placeholder — pinned bottom-centre, matching the
            real chip's anchor. Static muted fill (no shimmer) keeps it
            from competing with the card image's shimmer. */}
        <div className="bg-base-content/30 bottom-4.5 absolute left-1/2 z-10 h-6 w-32 -translate-x-1/2 rounded-full" />
      </div>

      {showPanel && (
        // bg-base-200 (transparent border) gives a soft surface contrast
        // against the body so the panel reads as its own area without a thin
        // border-line at the seam where it overlaps the card image. The
        // border still occupies the real cell's 1px box so heights match.
        <div className="rounded-b-box bg-base-200 -mt-4 flex w-full flex-1 flex-col border border-transparent p-2 pt-2">
          <div className="flex flex-1 flex-col gap-3 pt-3 lg:pt-4">
            {showFooter && (
              // min-h matches the real footer row (UserHoverCard xs = 1.6rem),
              // measured — the skeleton bars alone are shorter and every row
              // below would shift up by the difference when content lands.
              <div className="flex min-h-[1.6rem] items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  {!hideUser && (
                    <div className="skeleton h-5 w-5 shrink-0 rounded-full" />
                  )}
                  <div className="skeleton h-3 w-20" />
                </div>
              </div>
            )}
            {!hideAction && (
              <div className="mt-auto">
                <div className="skeleton btn btn-sm w-full" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TradePreviewCardSkeleton;
